import React from 'react';
import { SearchAsyncModal } from 'src/modals/SearchAsyncModal';
import classNames from 'classnames';

import { useAccountAction, useAccountSelect } from 'src/hooks/useAccount';
import { useOutlet, useLocation } from 'react-router-dom';
import {
  SwitchTransition,
  CSSTransition,
} from 'react-transition-group';
import { Header } from 'src/components/Header';
import { NavigationMobile } from 'src/components/NavigationMobile';
import { Navigation } from 'src/components/Navigation';
import styles from './RootLayout.module.scss';
import { usePaymentAction } from 'src/hooks/usePayment';
import { useModalAppInstall } from 'src/hooks/route/useModalAppInstall';
import { useSearchQuery } from 'src/hooks/useSearchQuery';
import { HeloBonusModal } from 'src/components/HeloBonusModal';
import { ReferralReward } from 'src/components/ReferralRewards';
import { Unboarding } from 'src/components/Unboarding';
import { AgeConsentModal } from 'src/modals/AgeConsentModal';
import { DailyRewardModal } from 'src/components/DailyReward';

export const RootLayout = () => {
  const location = useLocation();
  const isClient = useAccountSelect.isClient();
  const isUser = useAccountSelect.isUser();
  const isAnonymous = useAccountSelect.isAnonymous();
  const anonymousNeedsAuth = useAccountSelect.anonymousNeedsAuth();
  const adultConfirmed = useAccountSelect.confirmAdult();

  const modalInstallApp = useModalAppInstall()
  const pageRef = React.useRef<HTMLDivElement | null>(null);
  const outlet = useOutlet();
  const sessionSetupStatus = useAccountAction.sessionSetup.status()
  const accountSetupStatus = useAccountAction.accountSetup.status()
  const [accountSetupIsFullfiled, setAccountSetupIsFullfiled] = React.useState(false);
  // Only wait for accountSetup if it was actually started (for existing Firebase + JWT users)
  // For new anonymous users or new Firebase logins, sessionSetup handles everything
  const needsAccountSetup = accountSetupStatus.isPending || accountSetupStatus.isFulfilled || accountSetupStatus.isRejected
  const isUserComplited = needsAccountSetup ? accountSetupIsFullfiled : true
  const isFullfiled = sessionSetupStatus.isFulfilled && isUserComplited
  const sessionSetupAction = useAccountAction.sessionSetup();

  const getProducts = usePaymentAction.getProducts()
  const search = useSearchQuery()


  React.useEffect(() => {
    sessionSetupAction();
  }, []);

  React.useEffect(()=>{
    if (accountSetupIsFullfiled === false && accountSetupStatus.isFulfilled){
      setAccountSetupIsFullfiled(true)
    }
  },[accountSetupIsFullfiled, accountSetupStatus.isFulfilled])

  React.useEffect(() => {
    if (modalInstallApp.isNeedNotify) {
      modalInstallApp.openNotifyAlert()
    }
  }, [modalInstallApp.isNeedNotify])

  React.useEffect(() => {
    if (isUser && isFullfiled) {
      // appReady();
      getProducts()
    }
  }, [isUser, isFullfiled]);

  // Only redirect to auth for specific modals requiring real account
  React.useEffect(() => {
    if (!isUser) {
      const notRedirect = ['auth', 'login', 'sign-up','sign-in', 'email-verification', 'recovery-password']
      const requiresAuthModals = ['profile-edit', 'account-settings'] // Specific modals only
      const { modal } = search.query

      // Only show auth modal if trying to access auth-required features
      if (modal && !notRedirect.includes(modal) && requiresAuthModals.includes(modal)) {
        const getRedirect = () => {
          if (!notRedirect.includes(modal)) {
            return modal
          }
          return null
        }

        search.setQuery({
          modal: 'auth',
          redirectModal: getRedirect()
        })
      }
    }
  }, [isUser, search.query])

  // Show auth modal for anonymous users who sent a message
  const currentModal = search.query.modal;
  const hasTriggeredAuthModal = React.useRef(false);

  React.useEffect(() => {
    if (isFullfiled && isAnonymous && anonymousNeedsAuth && currentModal !== 'auth' && !hasTriggeredAuthModal.current) {
      hasTriggeredAuthModal.current = true;
      search.setQuery({
        modal: 'auth'
      });
    }

    if (currentModal !== 'auth' && hasTriggeredAuthModal.current && !anonymousNeedsAuth) {
      hasTriggeredAuthModal.current = false;
    }
  }, [isFullfiled, currentModal, isAnonymous, anonymousNeedsAuth])

  const isLoading = !isFullfiled


  return (
    <>
      <div className={classNames(styles.loader, { [styles.loader__loading]: isLoading })}>

        <div className={styles.logo}>
          <svg fill="none" height="300" viewBox="0 0 300 300" width="300" ><filter id="a" color-interpolation-filters="sRGB" filterUnits="userSpaceOnUse" height="229.351" width="254.351" x="23.3247" y="43.6623"><feFlood flood-opacity="0" result="BackgroundImageFix" /><feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" /><feOffset dy="8.33766" /><feGaussianBlur stdDeviation="8.33766" /><feComposite in2="hardAlpha" operator="out" /><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" /><feBlend in2="BackgroundImageFix" mode="normal" result="effect1_dropShadow_389_3258" /><feBlend in="SourceGraphic" in2="effect1_dropShadow_389_3258" mode="normal" result="shape" /><feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" /><feOffset dy="8.33766" /><feGaussianBlur stdDeviation="8.33766" /><feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" /><feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.6 0" /><feBlend in2="shape" mode="normal" result="effect2_innerShadow_389_3258" /><feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" /><feOffset dy="-8.33766" /><feGaussianBlur stdDeviation="5.55844" /><feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" /><feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.26 0" /><feBlend in2="effect2_innerShadow_389_3258" mode="normal" result="effect3_innerShadow_389_3258" /><feColorMatrix in="SourceAlpha" result="hardAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" /><feOffset dy="1.38961" /><feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" /><feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0" /><feBlend in2="effect3_innerShadow_389_3258" mode="normal" result="effect4_innerShadow_389_3258" /></filter><linearGradient id="b" gradientUnits="userSpaceOnUse" x1="150.5" x2="150.5" y1="52" y2="248"><stop offset="0" stop-color="#ffba2c" /><stop offset="1" stop-color="#ff8a00" /></linearGradient><g filter="url(#a)"><path d="m157.351 247.009c-3.757 1.321-9.945 1.321-13.702 0-32.045-10.901-103.649-56.378-103.649-133.456 0-34.0249 27.5145-61.553 61.438-61.553 20.111 0 37.902 9.6899 49.062 24.6652 5.677-7.6432 13.072-13.8552 21.592-18.1384 8.519-4.2831 17.927-6.5184 27.47-6.5268 33.924 0 61.438 27.5281 61.438 61.553 0 77.078-71.604 122.555-103.649 133.456z" fill="url(#b)" /></g></svg>
          <div className={styles.logo__title}>
            {[...'Romantic'].map((letter, index) => (
              <span className={styles.logo__letter} style={{ animationDelay: `0.${index}s` }}>{letter}</span>
            ))} <span className={styles.logo__primary}>{[...'AI'].map((letter, index) => (
              <span className={styles.logo__letter} style={{ animationDelay: `0.${index + 8}s` }}>{letter}</span>
            ))}</span>
          </div>
        </div>
      </div>

      {isFullfiled && (
        <>
          <AgeConsentModal />
          {adultConfirmed && (
            <>
              {isUser && (
                <HeloBonusModal/>
              )}
              <Unboarding />
              <SearchAsyncModal />
              {isUser && (
                <DailyRewardModal />
              )}
              <ReferralReward/>
              {(isUser || isClient) && (


                <div className={classNames(styles.main, { [styles.loaded]: !isLoading })}>
                  <Header />

                  {/* <ResubscriptionAlert /> */}
                  {/* <PaywallSuperOfferAsync /> */}

                  <Navigation />


                  <div id='root-layout-scroll' className={styles.scrollbar}>
                    <div className={styles.content}>
                      <SwitchTransition>
                        <CSSTransition
                          key={location.pathname}
                          nodeRef={pageRef}
                          timeout={75}
                          classNames={'page'}
                          unmountOnExit
                        >
                          {() => (
                            <div ref={pageRef} className={`page`}>
                              {outlet}
                            </div>
                          )}
                        </CSSTransition>
                      </SwitchTransition>
                    </div>
                  </div>
                  <NavigationMobile />
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default RootLayout;
